import type React from 'react';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

type Anchor = 'left' | 'right' | 'top' | 'bottom';
type Variant = 'temporary' | 'permanent';

export interface DrawerProps {
  /** Controls the open state of the drawer */
  open?: boolean;
  /** Callback fired when the drawer needs to be closed */
  onClose?: (event: React.MouseEvent | React.KeyboardEvent) => void;
  /** Side from which the drawer will appear */
  anchor?: Anchor;
  /** The variant to use */
  variant?: Variant;
  /** Drawer contents */
  children?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the backdrop in temporary variant */
  backdrop?: boolean;
}

interface PositionConfig {
  [key: string]: string;
  left: string;
  right: string;
  top: string;
  bottom: string;
}

const Drawer: React.FC<DrawerProps> = ({
  open = false,
  onClose,
  anchor = 'left',
  variant = 'temporary',
  children,
  className = '',
  backdrop = true,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(open);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  // Handle click outside for temporary drawers
  const handleBackdropClick = (e: React.MouseEvent): void => {
    if (variant === 'temporary' && onClose) {
      onClose(e);
    }
  };

  // Calculate position classes based on anchor
  const getPositionClasses = (): string => {
    const positions: PositionConfig = {
      left: 'left-0',
      right: 'right-0',
      top: 'top-0 w-full',
      bottom: 'bottom-0 w-full',
    };
    return positions[anchor] || positions.left;
  };

  // Calculate transform classes for sliding animation
  const getTransformClasses = (): string => {
    const transforms: PositionConfig = {
      left: isOpen ? 'translate-x-0' : '-translate-x-full',
      right: isOpen ? 'translate-x-0' : 'translate-x-full',
      top: isOpen ? 'translate-y-0' : '-translate-y-full',
      bottom: isOpen ? 'translate-y-0' : 'translate-y-full',
    };
    return transforms[anchor] || transforms.left;
  };

  // Calculate height/width classes based on anchor
  const getSizeClasses = (): string => {
    return ['top', 'bottom'].includes(anchor) ? 'h-[300px]' : 'h-full w-[280px]';
  };

  // // Render permanent drawer directly
  // if (variant === "permanent") {
  //   return <div className={baseClasses}>{children}</div>;
  // }

  return (
    <>
      {/* Backdrop */}
      {backdrop && isOpen && variant === 'temporary' && (
        // biome-ignore lint/a11y/useKeyWithClickEvents: no
        <div
          className='fixed inset-0 bg-black/50 transition-opacity z-40'
          onClick={handleBackdropClick}
        />
      )}

      {/* Drawer */}
      <div
        className={twMerge(
          'fixed',
          getPositionClasses(),
          getSizeClasses(),
          'transition-transform duration-300 ease-in-out',
          getTransformClasses(),
          variant !== 'temporary' ? 'relative transform-none' : '',
          className,
          'z-50'
        )}
      >
        {children}
      </div>
    </>
  );
};
Drawer.displayName = 'Drawer';

export default Drawer;
